import { lazy, Suspense, useEffect, useMemo, useState } from 'react'
import { useRouter, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { areaLabel, useCourse, type AreaRow } from '../data/geo.ts'
import { areaPlace, guidance, targetPoint } from '../data/guide.ts'
import { geoErrorState } from '../data/livePosition.ts'
import { useLocationOn } from '../data/location.ts'
import { areaShort } from '../data/planUi.ts'
import { boundsOf, formatLength, type Pos } from '../lib/geo.ts'
import { useBaseMap } from '../map/useBaseMap.ts'
import { isOpen } from '../sync/projectors.ts'
import { usePageVisible } from '../ui/hooks.ts'
import { Picto } from '../ui/pictos.tsx'

const MapView = lazy(() => import('../map/MapView.tsx'))
const GuideLayer = lazy(() => import('../map/MapView.tsx').then((m) => ({ default: m.GuideLayer })))

/** How often my position is taken while this page is on screen: one `getCurrentPosition` at a time (watchPosition stays banned). */
export const GUIDE_EVERY_MS = 5_000
type GeoState = 'waiting' | 'ok' | 'denied' | 'unavailable'

/** "Me guider" (M19): the way to the area of one's task, in a straight line, from my position on this phone. The position is only used here, on
 *  screen: it is NOT shared (sharing is the running timer's, and only that). The course's paths are what one follows: the screen says so.
 *  Where the club has location OFF (M21), the same page is "Voir sur la carte": the area centred and highlighted on the map, with its name and where it
 *  sits (loop, hole: its ancestors), and nothing of the phone's position: it is never asked for, and no distance or direction is shown. */
export function MapGuidePage() {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const router = useRouter()
  const search = useSearch({ strict: false }) as { area?: string }
  const course = useCourse(lang)
  const base = useBaseMap()
  const visible = usePageVisible()
  const location = useLocationOn()
  const [fix, setFix] = useState<{ pos: Pos; accuracy: number } | null>(null)
  const [geo, setGeo] = useState<GeoState>('waiting')

  const area = course.areas.find((a) => a.id === search.area)
  const version = course.areaVersions.find((v) => v.area_id === search.area && isOpen(v))
  const target = useMemo(() => targetPoint(version?.geom), [version])
  const byId = useMemo(() => new Map(course.areas.map((a) => [a.id, a as AreaRow])), [course.areas])
  const fit = useMemo(() => (version ? boundsOf([version.geom]) : null), [version])
  const selected = useMemo(() => (search.area ? { kind: 'area' as const, id: search.area } : null), [search.area])

  useEffect(() => {
    if (!visible || !location) return                                  // M21: location off, the phone is never asked
    if (!navigator.geolocation) { setGeo('unavailable'); return }
    let stopped = false, busy = false
    const take = () => {
      if (busy || stopped) return
      busy = true
      navigator.geolocation.getCurrentPosition(
        (p) => { busy = false; if (stopped) return; setFix({ pos: [p.coords.longitude, p.coords.latitude], accuracy: p.coords.accuracy }); setGeo('ok') },
        (e) => { busy = false; if (stopped) return; setGeo(geoErrorState(e.code)) },
        { enableHighAccuracy: true, maximumAge: GUIDE_EVERY_MS, timeout: 20_000 },
      )
    }
    take()
    const id = setInterval(take, GUIDE_EVERY_MS)
    return () => { stopped = true; clearInterval(id) }
  }, [visible, location])

  const g = location && fix && target ? guidance(fix.pos, target) : null
  const name = area ? `${areaShort(area, byId, lang)}${area.code ? ` · ${area.code}` : ''}` : ''
  const state = !course.ready ? 'loading' : !area ? 'missing' : !target ? 'no_shape' : location ? geo : 'view'
  const place = area ? [...areaPlace(area as AreaRow, byId).map((p) => areaLabel(p, lang)), t(`areaType.${area.area_type_code}`)].join(' › ') : ''
  return (
    <section className="flex flex-col gap-3" data-testid="guide-page" data-area={search.area ?? ''} data-state={state} data-mode={location ? 'guide' : 'view'}>
      <button className="inline-flex min-h-14 items-center self-start px-1 text-sm font-semibold underline" onClick={() => router.history.back()} data-testid="guide-back">← {t('common.back')}</button>
      <h1 className="flex items-center gap-2 text-2xl font-bold"><Picto name="map" className="size-7 text-brand dark:text-fairway" />{location ? t('guide.title') : t('guide.view')}</h1>
      {!course.ready ? <p>{t('common.loading')}</p> : !area ? (
        <p className="rounded-lg border-2 border-amber-400 bg-amber-50 p-3 text-amber-900 dark:border-amber-600 dark:bg-amber-950/40 dark:text-amber-100" role="status" data-testid="guide-missing">{t('guide.missing')}</p>
      ) : (
        <>
          <p className="text-lg font-semibold" data-testid="guide-target">{location ? t('guide.to', { area: name }) : name}</p>
          {!location ? (
            // M21: the area and where it sits, nothing of the phone (no position, no distance, no direction)
            <div className="card flex flex-col gap-1" data-testid="guide-card">
              <p className="font-medium" data-testid="guide-place">{place}</p>
              {!target && <p data-testid="guide-no-shape">{t('guide.noShape')}</p>}
              <p className="meta" data-testid="guide-view-note">{t('guide.viewNote')}</p>
            </div>
          ) : (
            <div className="card flex flex-col gap-1" data-testid="guide-card">
              {!target ? <p data-testid="guide-no-shape">{t('guide.noShape')}</p>
                : g ? (
                  <>
                    <span className="text-4xl font-bold tabular-nums text-brand dark:text-fairway" data-testid="guide-distance" data-m={Math.round(g.distanceM)}>{t('guide.distance', { d: formatLength(g.distanceM, lang) })}</span>
                    <span className="text-xl font-semibold" data-testid="guide-direction" data-dir={g.direction}>{t('guide.direction', { dir: t(`guide.dir.${g.direction}`) })}</span>
                    {fix && <span className="text-sm opacity-75" data-testid="guide-accuracy">{t('guide.accuracy', { m: new Intl.NumberFormat(lang, { maximumFractionDigits: 0 }).format(fix.accuracy) })}</span>}
                  </>
                ) : (
                  <p role="status" data-testid="guide-geo" data-geo={geo}>{geo === 'denied' ? t('guide.denied') : geo === 'unavailable' ? t('guide.unavailable') : t('guide.waiting')}</p>
                )}
              <p className="text-sm font-medium" data-testid="guide-note">{t('guide.note')}</p>
              <p className="meta">{t('guide.notShared')}</p>
            </div>
          )}
          <Suspense fallback={<div className="grid h-[55dvh] place-items-center rounded-xl border">{t('common.loading')}</div>}>
            <MapView className="h-[55dvh] min-h-72 overflow-hidden rounded-xl border-2" base={base.archive} areas={course.areaCollection} infra={course.infraCollection}
              showInfra={false} selected={selected} fit={fit} interactive={false}>
              <Suspense fallback={null}><GuideLayer me={location ? fix : null} target={target} /></Suspense>
            </MapView>
          </Suspense>
        </>
      )}
    </section>
  )
}
